import React, {
  useEffect,
  useMemo,
  useState
} from "react";

import { auth } from "../firebase";

const API =
  process.env.REACT_APP_SELLER_API ||
  "https://gallery.pitics.com";

const PER_PAGE = 100;

// Only this productId is eligible for the "Generate Labels" action
const PLABEL_PRODUCT_ID = "2202262021";

const modalInput = {
  width: "100%",
  padding: 12,
  borderRadius: 12,
  border: "1px solid #d1d5db",
  marginBottom: 14,
  boxSizing: "border-box"
};

export default function SellerOrders() {

  const [orders, setOrders] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [updatingId, setUpdatingId] =
    useState("");

  const [loadingId, setLoadingId] =
    useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [photoDownloadKey, setPhotoDownloadKey] = useState("");

  

  /* =====================================================
     SHIPPING DETAILS
     Default = C (Completely Manual)
     A/B are displayed for future integrations only.
  ===================================================== */
  const [shippingDialog, setShippingDialog] = useState(null);
  const [shippingMethod, setShippingMethod] = useState("C");
  const [courierService, setCourierService] = useState("");
  const [shipmentId, setShipmentId] = useState("");
  const [savingShipment, setSavingShipment] = useState(false);

  const courierOptions = [
    "India Post",
    "DTDC",
    "Delhivery",
    "Blue Dart",
    "Ecom Express",
    "Professional Couriers",
    "Other"
  ];
const [page, setPage] =
    useState(1);

  const [expandedOrders,
    setExpandedOrders] =
    useState({});

  /* =====================================================
     LOAD ORDERS
  ===================================================== */

  const loadOrders =
    async () => {

      try {

        setLoading(true);

        const user =
  auth.currentUser;

if (!user) {
  return;
}

const token =
  await user.getIdToken();

        const res =
          await fetch(
            `${API}/api/seller/orders`,
            {
              headers: {
                "X-Firebase-Authorization":
                  token
              }
            }
          );

        const data =
          await res.json();

        if (!data.success) {

          throw new Error(
            data.error ||
            "Failed to load orders"
          );
        }

        const sorted =
          Array.isArray(
            data.orders
          )
            ? [...data.orders]
                .sort(
                  (a, b) => {

                    const ad =
                      a.createdAt
                        ? new Date(
                            a.createdAt
                          ).getTime()
                        : 0;

                    const bd =
                      b.createdAt
                        ? new Date(
                            b.createdAt
                          ).getTime()
                        : 0;

                    return bd - ad;
                  }
                )
            : [];

        setOrders(sorted);

      } catch (err) {

        console.error(err);

        alert(
          err.message ||
          "Failed to load seller orders"
        );

      } finally {

        setLoading(false);
      }
    };

  useEffect(() => {
    loadOrders();
  }, []);

  /* =====================================================
     STATUS UPDATE
  ===================================================== */

  const updateStatus =
    async (
      orderId,
      status,
      shipping = null
    ) => {
      try {
        setUpdatingId(orderId);

        const token =
          await auth.currentUser.getIdToken();

        const body = { status };

        if (status === "shipped") {
          body.shippingMethod =
            shipping?.shippingMethod || "C";
          body.courierService =
            shipping?.courierService || "";
          body.shipmentId =
            shipping?.shipmentId || "";
        }

        const res = await fetch(
          `${API}/api/seller/orders/${orderId}/status`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Firebase-Authorization": token
            },
            body: JSON.stringify(body)
          }
        );

        let data;
        try {
          data = await res.json();
        } catch {
          throw new Error("Invalid server response");
        }

        if (!res.ok || !data.success) {
          throw new Error(
            data.error || "Failed to update status"
          );
        }

        await loadOrders();
      } catch (err) {
        console.error(err);
        alert(
          err.message ||
          "Status update failed"
        );
        throw err;
      } finally {
        setUpdatingId("");
      }
    };

  const openShippingDialog = (order) => {
    setShippingDialog(order);
    setShippingMethod("C");
    setCourierService("");
    setShipmentId("");
  };

  const closeShippingDialog = () => {
    if (savingShipment) return;
    setShippingDialog(null);
    setShippingMethod("C");
    setCourierService("");
    setShipmentId("");
  };

  const confirmShipment = async () => {
    const sid = shipmentId.trim();
    const courier = courierService.trim();

    if (!sid) {
      alert("Enter the shipment ID.");
      return;
    }

    if (!courier) {
      alert("Select the courier service.");
      return;
    }

    if (shippingMethod !== "C") {
      alert(
        "Courier integrations A and B are not implemented yet. Please use C — Completely Manual."
      );
      return;
    }

    try {
      setSavingShipment(true);

      await updateStatus(
        shippingDialog.orderId,
        "shipped",
        {
          shippingMethod: "C",
          courierService: courier,
          shipmentId: sid
        }
      );

      setShippingDialog(null);
      setShippingMethod("C");
      setCourierService("");
      setShipmentId("");
    } catch (err) {
      // updateStatus already shows the server error.
    } finally {
      setSavingShipment(false);
    }
  };


/* =====================================================
   DOWNLOAD CUSTOMER PHOTOS
   Calls the authenticated seller endpoint to authorize, sign, and count downloads.
===================================================== */
const downloadCustomerPhotos = async (order, item, itemIndex) => {
  const key = `${order.orderId}:${itemIndex}`;
  try {
    setPhotoDownloadKey(key);
    const user = auth.currentUser;
    if (!user) throw new Error("Please sign in again.");
    const token = await user.getIdToken();
    const photos = Array.isArray(item?.uploadedAssets?.customImages) ? item.uploadedAssets.customImages : [];
    if (!photos.length) throw new Error("No customer photos are attached to this item.");
    const response = await fetch(`${API}/api/seller/orders/${encodeURIComponent(order.orderId)}/photos/download`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Firebase-Authorization": token },
      body: JSON.stringify({ itemIndex, photoIndexes: photos.map((_, index) => index) })
    });
    const result = await response.json();
    if (!response.ok || !result.success) throw new Error(result.error || "Could not prepare photo downloads.");
    const signedPhotos = Array.isArray(result.photos) ? result.photos : [];
    if (!signedPhotos.length) throw new Error("No downloadable customer photos were returned.");
    signedPhotos.forEach((photo, index) => {
      if (!photo.url) return;
      const link = document.createElement("a");
      link.href = photo.url;
      link.download = photo.fileName || `order_${order.orderId}_item_${itemIndex + 1}_photo_${index + 1}`;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      link.remove();
    });
    await loadOrders();
  } catch (err) {
    console.error("Customer photo download failed:", err);
    alert(err.message || "Failed to download customer photos.");
  } finally {
    setPhotoDownloadKey("");
  }
};

/* =====================================================
   DOWNLOAD FILE (invoice)
===================================================== */

const downloadFile =
  async (
    orderId,
    type
  ) => {

    try {

      const token =
        await auth.currentUser
          .getIdToken();

      const url =
        `${API}/api/seller/orders/${encodeURIComponent(orderId)}/${type}`;

      const res = await fetch(url, {
        headers: { "X-Firebase-Authorization": token }
      });

if (!res.ok) {

  throw new Error(
    `Failed to download ${type}`
  );
}

const blob =
  await res.blob();

const blobUrl =
  window.URL.createObjectURL(
    blob
  );

const a =
  document.createElement("a");

a.href = blobUrl;

a.download =
  `${orderId}_${type}.pdf`;

document.body.appendChild(a);

a.click();

a.remove();

window.URL.revokeObjectURL(
  blobUrl
);

    } catch (err) {

      console.error(err);

      alert(
        err.message ||
        `Failed to download ${type}`
      );
    }
  };

  /* =====================================================
     GENERATE LABELS (real-time, plabel service)
  ===================================================== */

  const generateLabels =
    async (orderId) => {

      try {

        setLoadingId(orderId);

        const token =
          await auth.currentUser
            .getIdToken();

        const res =
          await fetch(
            `${API}/api/plabel/generate`,
            {
              method: "POST",

              headers: {
                "Content-Type":
                  "application/json",

                "X-Firebase-Authorization":
                  token
              },

              body:
                JSON.stringify({
                  orderId
                })
            }
          );

        if (!res.ok) {

          throw new Error(
            "Failed to generate labels"
          );
        }

        const blob =
          await res.blob();

        const pdfBlob =
          new Blob(
            [blob],
            {
              type:
                "application/pdf"
            }
          );

        const blobUrl =
          window.URL.createObjectURL(
            pdfBlob
          );

        /* open in a new tab instead of forcing a download */
        const a = document.createElement("a");
a.href = blobUrl;
a.download = `${orderId}_labels.pdf`;

document.body.appendChild(a);
a.click();
a.remove();

window.URL.revokeObjectURL(blobUrl);

      } catch (err) {

        console.error(err);

        alert(
          err.message ||
          "Failed to generate labels"
        );

      } finally {

        setLoadingId(null);
      }
    };

  /* =====================================================
     PAGINATION
  ===================================================== */

  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      const status = String(order.status || order.fulfilmentStatus || "pending").toLowerCase();
      if (statusFilter === "all") return true;
      if (statusFilter === "ordered") return ["ordered", "ready", "processed", "shipped"].includes(status);
      return status === statusFilter;
    });
  }, [orders, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / PER_PAGE));
  const paginatedOrders = useMemo(() => {
    const start = (page - 1) * PER_PAGE;
    return filteredOrders.slice(start, start + PER_PAGE);
  }, [filteredOrders, page]);

  /* =====================================================
     STATUS COLORS
  ===================================================== */

  const statusColors = {

    pending: {
      bg: "#fef3c7",
      text: "#92400e"
    },

    processed: {
      bg: "#dbeafe",
      text: "#1d4ed8"
    },

    shipped: {
      bg: "#ede9fe",
      text: "#6d28d9"
    },

    delivered: {
      bg: "#dcfce7",
      text: "#15803d"
    },

    cancelled: {
      bg: "#fee2e2",
      text: "#dc2626"
    }
  };

  /* =====================================================
     LOADING
  ===================================================== */

  if (loading) {

    return (

      <div
        style={{
          padding: 24,
          fontSize: 18,
          fontWeight: 800
        }}
      >
        Loading orders...
      </div>
    );
  }

  /* =====================================================
     EMPTY
  ===================================================== */

  if (
    orders.length === 0
  ) {

    return (

      <div
        style={{
          background: "#fff",
          borderRadius: 24,
          padding: 30,
          border:
            "1px solid #e5e7eb",
          fontWeight: 800,
          boxShadow:
            "0 10px 30px rgba(0,0,0,.05)"
        }}
      >
        No seller orders found
      </div>
    );
  }

  /* =====================================================
     UI
  ===================================================== */

  return (

    <div
      style={{
        display: "grid",
        gap: 18
      }}
    >

      {/* =========================================
         HEADER SUMMARY
      ========================================= */}

      <div
        style={{
          display: "flex",
          justifyContent:
            "space-between",
          alignItems:
            "center",
          flexWrap: "wrap",
          gap: 12,
          background:
            "#fff",
          padding: 20,
          borderRadius: 24,
          border:
            "1px solid #e5e7eb",
          boxShadow:
            "0 8px 24px rgba(0,0,0,.05)"
        }}
      >

        <div>

          <div
            style={{
              fontSize: 26,
              fontWeight: 900
            }}
          >
            🧾 Seller Orders
          </div>

          <div
            style={{
              marginTop: 8,
              color: "#555",
              fontWeight: 600
            }}
          >
            Total Orders:
            {" "}
            {orders.length}
          </div>
        </div>

        <div
          style={{
            background:
              "#eff6ff",
            color:
              "#1d4ed8",
            padding:
              "10px 18px",
            borderRadius: 999,
            fontWeight: 800
          }}
        >
          Page {page} / {totalPages}
        </div>
      </div>

      {/* ORDER STATUS FILTERS */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {[["all", "All Orders"], ["ordered", "Ordered"], ["pending", "Pending"], ["delivered", "Delivered"], ["cancelled", "Cancelled"]].map(([value, label]) => {
          const count = value === "all" ? orders.length : orders.filter((order) => {
            const status = String(order.status || order.fulfilmentStatus || "pending").toLowerCase();
            return value === "ordered" ? ["ordered", "ready", "processed", "shipped"].includes(status) : status === value;
          }).length;
          const active = statusFilter === value;
          return <button key={value} type="button" aria-pressed={active}
            onClick={() => { setStatusFilter(value); setPage(1); }}
            style={{ border: active ? "1px solid #1d4ed8" : "1px solid #d1d5db", background: active ? "#eff6ff" : "#fff", color: active ? "#1d4ed8" : "#374151", borderRadius: 999, padding: "9px 14px", fontWeight: 800, cursor: "pointer" }}>
            {label} ({count})
          </button>;
        })}
      </div>

      {filteredOrders.length === 0 ? (
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 16, padding: 24, color: "#64748b", fontWeight: 700 }}>
          No {statusFilter === "all" ? "seller" : statusFilter} orders found.
        </div>
      ) : null}

      {/* =========================================
         ORDERS
      ========================================= */}

      {paginatedOrders.map(
        (
          o,
          index
        ) => {

          const status =
            (
              o.status ||
              "pending"
            )
              .toLowerCase();

          const statusStyle =
            statusColors[
              status
            ] || {

              bg:
                "#f3f4f6",

              text:
                "#111827"
            };

          const rawImage =
  o.items?.[0]
    ?.productImagePath || "";

const firstImage =

  rawImage

    ? rawImage.startsWith(
        "http"
      )

        ? rawImage

        : `https://firebasestorage.googleapis.com/v0/b/piticsjgat.firebasestorage.app/o/${encodeURIComponent(rawImage)}?alt=media`

    : "/logo.png";

          const canGenerateLabels =

            o.items?.some(
              (x) =>
                String(
                  x.productId
                ) ===
                PLABEL_PRODUCT_ID
            );

          return (

            <div
              key={
                o.orderId ||
                index
              }
              style={{
                background:
                  "#fff",
                borderRadius: 26,
                border:
                  "1px solid #e5e7eb",
                overflow:
                  "hidden",
                boxShadow:
                  "0 10px 30px rgba(0,0,0,.05)"
              }}
            >

              {/* =====================================
                 TOP SUMMARY ROW
              ===================================== */}

              <div
                style={{
                  padding: 18,
                  display: "flex",
                  gap: 16,
                  alignItems:
                    "center",
                  flexWrap:
                    "wrap"
                }}
              >

                {/* SERIAL */}

                <div
                  style={{
                    minWidth: 44,
                    height: 44,
                    borderRadius:
                      999,
                    background:
                      "#111827",
                    color:
                      "#fff",
                    display:
                      "flex",
                    alignItems:
                      "center",
                    justifyContent:
                      "center",
                    fontWeight: 900
                  }}
                >
                  {(
                    (page - 1) *
                    PER_PAGE
                  ) +
                    index +
                    1}
                </div>

                {/* PRODUCT IMAGE */}

                <img
                  src={
                    firstImage
                  }
                  alt=""
                  onError={(e) => {
                    e.target.src =
                     "/logo.png";
                  }}
                  style={{
                    width: 70,
                    height: 70,
                    objectFit:
                      "cover",
                    borderRadius: 14,
                    background:
                      "#f8fafc",
                    border:
                      "1px solid #e5e7eb"
                  }}
                />

                {/* DETAILS */}

                <div
                  style={{
                    flex: 1,
                    minWidth: 240
                  }}
                >

                  <div
                    style={{
                      fontWeight: 900,
                      fontSize: 18
                    }}
                  >
                    Order #
                    {o.orderId}
                  </div>

                  <div
                    style={{
                      marginTop: 6,
                      color: "#555"
                    }}
                  >
                    {o.customerName ||
                      "-"}
                  </div>

                  <div
                    style={{
                      marginTop: 4,
                      color: "#6b7280",
                      fontSize: 14
                    }}
                  >
                    {o.createdAt
                      ? new Date(
                          o.createdAt
                        ).toLocaleString()
                      : "-"}
                  </div>
                </div>

                {/* ORDER INFO */}

                <div
                  style={{
                    display:
                      "grid",
                    gap: 6
                  }}
                >

                  <div
                    style={{
                      fontWeight: 700
                    }}
                  >
                    ₹
                    {o.total || 0}
                  </div>

                  <div
                    style={{
                      fontSize: 14,
                      color: "#666"
                    }}
                  >
                    {o.items
                      ?.length ||
                      0}
                    {" "}
                    Items
                  </div>

                  <div
                    style={{
                      background:
                        statusStyle.bg,
                      color:
                        statusStyle.text,
                      padding:
                        "8px 14px",
                      borderRadius:
                        999,
                      fontWeight: 800,
                      textTransform:
                        "capitalize",
                      textAlign:
                        "center"
                    }}
                  >
                    {status}
                  </div>
                </div>

                {/* ACTIONS */}

                <div
                  style={{
                    display:
                      "flex",
                    gap: 10,
                    flexWrap:
                      "wrap"
                  }}
                >

                  {o.invoicePath && (

  <button
    onClick={() =>
      downloadFile(
        o.orderId,
        "invoice"
      )
    }
    style={{
      ...miniBtn,
      background:
        "#111827",
      color:
        "#fff"
    }}
  >
    Download Invoice
  </button>
)}

                {canGenerateLabels && (

  <button
    disabled={
      loadingId ===
      o.orderId
    }
    onClick={() =>
      generateLabels(
        o.orderId
      )
    }
    style={{
      ...miniBtn,
      background:
        "#7c3aed",
      color:
        "#fff",
      opacity:
        loadingId ===
        o.orderId
          ? 0.7
          : 1
    }}
  >
    {loadingId ===
    o.orderId
      ? "Generating..."
      : "Generate Labels"}
  </button>
)}

                  <button
                    onClick={() =>
                      setExpandedOrders(
                        (
                          prev
                        ) => ({

                          ...prev,

                          [o.orderId]:
                            !prev[
                              o.orderId
                            ]
                        })
                      )
                    }
                    style={{
                      ...miniBtn,
                      background:
                        "#2563eb",
                      color:
                        "#fff"
                    }}
                  >
                    {expandedOrders[
                      o.orderId
                    ]
                      ? "Hide"
                      : "View"}
                  </button>
                </div>
              </div>

              {/* =====================================
                 EXPANDED DETAILS
              ===================================== */}

              {expandedOrders[
                o.orderId
              ] && (

                <div
                  style={{
                    borderTop:
                      "1px solid #e5e7eb",
                    padding: 20,
                    background:
                      "#fafafa"
                  }}
                >

                  {/* CUSTOMER */}

                  <div
                    style={{
                      display:
                        "grid",
                      gap: 10,
                      marginBottom: 24
                    }}
                  >

                    <div>
                      <strong>
                        Customer:
                      </strong>
                      {" "}
                      {o.customerName ||
                        "-"}
                    </div>

                    <div>
                      <strong>
                        Phone:
                      </strong>
                      {" "}
                      {o.customerPhone ||
                        "-"}
                    </div>

                    <div>
                      <strong>
                        Shipping:
                      </strong>
                      {" "}
                      {o.shippingAddress ||
                        "-"}
                    </div>
                  </div>

                  {/* ITEMS */}

                  <div
                    style={{
                      display:
                        "grid",
                      gap: 16
                    }}
                  >

                    {Array.isArray(
                      o.items
                    ) &&
                      o.items.map(
                        (
                          item,
                          i
                        ) => {

                          const photos =
                            item
                              ?.uploadedAssets
                              ?.customImages ||
                            [];

                          const customerText =

                            item
                              ?.customData
                              ?.customerName ||

                            item
                              ?.customData
                              ?.name ||

                            "";

                          return (

                            <div
                              key={i}
                              style={{
                                background:
                                  "#fff",
                                borderRadius:
                                  20,
                                padding: 16,
                                border:
                                  "1px solid #e5e7eb"
                              }}
                            >

                              <div
                                style={{
                                  display:
                                    "flex",
                                  gap: 16,
                                  flexWrap:
                                    "wrap"
                                }}
                              >

                                {/* PRODUCT IMAGE */}

                                <img
                                  src={

  item.productImagePath

    ? item.productImagePath.startsWith(
        "http"
      )

        ? item.productImagePath

        : `https://firebasestorage.googleapis.com/v0/b/piticsjgat.firebasestorage.app/o/${encodeURIComponent(item.productImagePath)}?alt=media`

    : "/logo.png"
}
                                  alt=""
                                  onError={(e) => {
                                    e.target.src =
                                      "/logo.png";
                                  }}
                                  style={{
                                    width: 80,
                                    height: 80,
                                    objectFit:
                                      "cover",
                                    borderRadius:
                                      14,
                                    background:
                                      "#f8fafc",
                                    border:
                                      "1px solid #e5e7eb"
                                  }}
                                />

                                {/* DETAILS */}

                                <div
                                  style={{
                                    flex: 1,
                                    minWidth: 240
                                  }}
                                >

                                  <div
                                    style={{
                                      fontWeight: 900,
                                      fontSize: 18,
                                      marginBottom: 10
                                    }}
                                  >
                                    #{i + 1}
                                    {" "}
                                    —
                                    {" "}
                                    {item.productName ||

                                      item.name ||

                                      "Unnamed Product"}
                                  </div>

                                  <div>
                                    <strong>
                                      Category:
                                    </strong>
                                    {" "}
                                    {item.collectionName ||
                                      "-"}
                                  </div>

                                  <div>
                                    <strong>
                                      Subcategory:
                                    </strong>
                                    {" "}
                                    {item.subcategory ||
                                      "-"}
                                  </div>

                                  <div>
                                    <strong>
                                      Product Type:
                                    </strong>
                                    {" "}
                                    {item.productType ||
                                      "-"}
                                  </div>

                                  <div>
                                    <strong>
                                      Product ID:
                                    </strong>
                                    {" "}
                                    {item.productId ||
                                      "-"}
                                  </div>

                                  <div>
                                    <strong>
                                      Seller:
                                    </strong>
                                    {" "}
                                    {item.sellerName ||
                                      "-"}
                                  </div>

                                  <div>
                                    <strong>
                                      Qty:
                                    </strong>
                                    {" "}
                                    {item.quantity ||
                                      1}
                                  </div>

                                  <div>
                                    <strong>
                                      Unit Price:
                                    </strong>
                                    {" "}
                                    ₹
                                    {item.unitPrice ||
                                      0}
                                  </div>

                                  <div>
                                    <strong>
                                      Total:
                                    </strong>
                                    {" "}
                                    ₹
                                    {item.total ||
                                      0}
                                  </div>

                                  {/* CUSTOM TEXT */}

                                  {customerText && (

                                    <div
                                      style={{
                                        marginTop: 12,
                                        padding:
                                          "10px 12px",
                                        background:
                                          "#eff6ff",
                                        borderRadius:
                                          12,
                                        color:
                                          "#1d4ed8",
                                        fontWeight: 700
                                      }}
                                    >
                                      ✍️
                                      {" "}
                                      {customerText}
                                    </div>
                                  )}

                                </div>
                              </div>

                              {/* CUSTOM PHOTOS */}

                              {photos.length >
                                0 && (

                                <div
                                  style={{
                                    marginTop: 18
                                  }}
                                >

                                  <div
                                    style={{
                                      marginBottom: 10,
                                      fontWeight: 800
                                    }}
                                  >
                                    Uploaded Photos ({photos.length})
                                  </div>
                                  <button type="button"
                                    disabled={photoDownloadKey === `${o.orderId}:${i}`}
                                    onClick={() => downloadCustomerPhotos(o, item, i)}
                                    style={{ ...miniBtn, background: "#0f766e", color: "#fff", marginBottom: 12, opacity: photoDownloadKey === `${o.orderId}:${i}` ? 0.65 : 1 }}>
                                    {photoDownloadKey === `${o.orderId}:${i}` ? "Preparing downloads…" : `Download ${photos.length} Customer Photo${photos.length === 1 ? "" : "s"}`}
                                  </button>

                                  <div
                                    style={{
                                      display:
                                        "flex",
                                      gap: 10,
                                      flexWrap:
                                        "wrap"
                                    }}
                                  >

                                    {photos
                                      .slice(
                                        0,
                                        4
                                      )
                                      .map(
                                        (
                                          img,
                                          idx
                                        ) => (

                                          <img
                                            key={idx}
                                           src={

  img.url || "/logo.png"
}
                                            alt=""
                                            onError={(e) => {
                                              e.target.src =
                                                "/logo.png";
                                            }}
                                            style={{
                                              width: 90,
                                              height: 90,
                                              objectFit:
                                                "cover",
                                              borderRadius:
                                                12,
                                              border:
                                                "1px solid #ddd"
                                            }}
                                          />
                                        )
                                      )}

                                    {photos.length >
                                      4 && (

                                      <div
                                        style={{
                                          width: 90,
                                          height: 90,
                                          borderRadius:
                                            12,
                                          background:
                                            "#f3f4f6",
                                          display:
                                            "flex",
                                          alignItems:
                                            "center",
                                          justifyContent:
                                            "center",
                                          fontWeight: 800
                                        }}
                                      >
                                        +
                                        {photos.length - 4}
                                      </div>
                                    )}

                                  </div>
                                </div>
                              )}

                            </div>
                          );
                        }
                      )}
                  </div>

                  {/* =========================================
                     SHIPMENT DETAILS
                  ========================================= */}
                  {(o.status === "shipped" ||
                    o.status === "delivered") &&
                    (o.shipmentId ||
                      o.courierService ||
                      o.shippingMethod) && (
                    <div
                      style={{
                        marginTop: 18,
                        padding: 16,
                        borderRadius: 16,
                        background: "#f8fafc",
                        border: "1px solid #e2e8f0"
                      }}
                    >
                      <div style={{ fontWeight: 900, marginBottom: 8 }}>
                        Shipment
                      </div>
                      <div style={{ display: "grid", gap: 6, fontSize: 14 }}>
                        {o.shippingMethod && (
                          <div>
                            <strong>Method:</strong>{" "}
                            {o.shippingMethod === "C"
                              ? "C — Completely Manual"
                              : o.shippingMethod}
                          </div>
                        )}
                        {o.courierService && (
                          <div>
                            <strong>Courier:</strong>{" "}
                            {o.courierService}
                          </div>
                        )}
                        {o.shipmentId && (
                          <div>
                            <strong>Shipment ID:</strong>{" "}
                            {o.shipmentId}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* STATUS ACTIONS */}

                  {o.status !==
                    "delivered" &&

                    o.status !==
                    "cancelled" && (

                    <div
                      style={{
                        marginTop: 24,
                        display:
                          "flex",
                        gap: 12,
                        flexWrap:
                          "wrap"
                      }}
                    >

                      <button
                        disabled={
                          updatingId ===
                          o.orderId
                        }
                        onClick={() =>
                          updateStatus(
                            o.orderId,
                            "processed"
                          )
                        }
                        style={
                          actionBtn
                        }
                      >
                        Processed
                      </button>

                      <button
                        disabled={
                          updatingId ===
                          o.orderId
                        }
                        onClick={() =>
                          openShippingDialog(o)
                        }
                        style={
                          actionBtn
                        }
                      >
                        Shipped
                      </button>

                      <button
                        disabled={
                          updatingId ===
                          o.orderId
                        }
                        onClick={() =>
                          updateStatus(
                            o.orderId,
                            "delivered"
                          )
                        }
                        style={
                          actionBtn
                        }
                      >
                        Delivered
                      </button>

                      <button
                        disabled={
                          updatingId ===
                          o.orderId
                        }
                        onClick={() =>
                          updateStatus(
                            o.orderId,
                            "cancelled"
                          )
                        }
                        style={{
                          ...actionBtn,
                          background:
                            "#dc2626"
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  )}

                </div>
              )}

            </div>
          );
        }
      )}

      {/* =========================================
         SHIPMENT MODAL
      ========================================= */}
      {shippingDialog && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            zIndex: 9999
          }}
        >
          <div
            style={{
              width: "min(560px, 100%)",
              background: "#fff",
              borderRadius: 24,
              padding: 24,
              boxShadow: "0 25px 80px rgba(0,0,0,.25)"
            }}
          >
            <h2 style={{ marginTop: 0 }}>
              Mark Order as Shipped
            </h2>

            <div style={{ color: "#6b7280", marginBottom: 18 }}>
              Order: <strong>{shippingDialog.orderId}</strong>
            </div>

            <label style={{ display: "block", fontWeight: 800, marginBottom: 7 }}>
              Shipping method
            </label>
            <select
              value={shippingMethod}
              onChange={(e) => setShippingMethod(e.target.value)}
              style={modalInput}
            >
              <option value="A" disabled>
                A — PITICS Courier Integration (Coming Soon)
              </option>
              <option value="B" disabled>
                B — Seller Courier Account Integration (Coming Soon)
              </option>
              <option value="C">
                C — Completely Manual
              </option>
            </select>

            <label style={{ display: "block", fontWeight: 800, marginBottom: 7 }}>
              Courier service
            </label>
            <select
              value={courierService}
              onChange={(e) => setCourierService(e.target.value)}
              style={modalInput}
            >
              <option value="">Select courier service</option>
              {courierOptions.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>

            <label style={{ display: "block", fontWeight: 800, marginBottom: 7 }}>
              Shipment ID / AWB / Tracking ID
            </label>
            <input
              value={shipmentId}
              onChange={(e) => setShipmentId(e.target.value)}
              placeholder="Enter shipment ID"
              style={modalInput}
            />

            <div
              style={{
                padding: 12,
                borderRadius: 12,
                background: "#fff7ed",
                color: "#9a3412",
                fontSize: 13,
                marginBottom: 18
              }}
            >
              A and B are displayed for future courier integrations
              and are not active yet. PITICS currently uses C —
              Completely Manual. PITICS does not provide COD.
            </div>

            <div style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
              flexWrap: "wrap"
            }}>
              <button
                onClick={closeShippingDialog}
                disabled={savingShipment}
                style={{
                  ...actionBtn,
                  background: "#e5e7eb",
                  color: "#111827"
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmShipment}
                disabled={savingShipment}
                style={actionBtn}
              >
                {savingShipment ? "Saving..." : "Confirm Shipped"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =========================================
         PAGINATION
      ========================================= */}

      {totalPages > 1 && (

        <div
          style={{
            display: "flex",
            justifyContent:
              "center",
            gap: 12,
            flexWrap: "wrap",
            marginTop: 10
          }}
        >

          <button
            disabled={
              page === 1
            }
            onClick={() =>
              setPage(
                page - 1
              )
            }
            style={
              pageBtn
            }
          >
            Prev
          </button>

          <div
            style={{
              padding:
                "10px 18px",
              fontWeight: 800
            }}
          >
            Page {page}
            {" "}
            /
            {" "}
            {totalPages}
          </div>

          <button
            disabled={
              page ===
              totalPages
            }
            onClick={() =>
              setPage(
                page + 1
              )
            }
            style={
              pageBtn
            }
          >
            Next
          </button>

        </div>
      )}

    </div>
  );
}

/* =====================================================
   BUTTONS
===================================================== */

const actionBtn = {

  border: "none",

  padding:
    "12px 18px",

  borderRadius: 14,

  cursor: "pointer",

  background:
    "#2563eb",

  color: "#fff",

  fontWeight: 800,

  fontSize: 14
};

const miniBtn = {

  border: "none",

  padding:
    "10px 14px",

  borderRadius: 12,

  cursor: "pointer",

  fontWeight: 800,

  fontSize: 13
};

const pageBtn = {

  border: "none",

  padding:
    "10px 18px",

  borderRadius: 12,

  cursor: "pointer",

  background:
    "#111827",

  color: "#fff",

  fontWeight: 800
};